// Explore-stats page: Gender composition in each Parliament.
// Recreates the Handbook page — % women/men per Parliament (stacked area)
// and women in Parliament by party per Parliament (stacked bar), with a
// Senators-only / all filter.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatStackedArea, StatStackedBar } from './charts';

export default function GenderComposition() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('all'); // 'all' | 'senators'

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await getExploreStat('gender-composition',
          scope === 'senators' ? { senators_only: true } : {});
        if (active) { setData(res); setError(null); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [scope]);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Gender composition in each Parliament"
      description="The percentage representation of women and men in each Parliament — for the first 16 Parliaments it was 0% women — and the number of women in Parliament by party over time."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="gender-composition"
    >
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(e, v) => v && setScope(v)}
        >
          <ToggleButton value="all">All parliamentarians</ToggleButton>
          <ToggleButton value="senators">Senators only</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Gender composition in each Parliament (%)
            </Typography>
            <StatStackedArea data={charts.composition || []} xKey="parliament"
                             keys={['Men %', 'Women %']} unit="%" />
          </Paper>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Women in Parliament by party in each Parliament
            </Typography>
            <StatStackedBar data={charts.women_by_party || []} xKey="parliament"
                            keys={charts.women_party_series || []} height={420} />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
